import {
    useCallback,
    useEffect,
    useRef,
    useState,
} from 'react';

interface UseWebSocketOptions {
    url: string;
    onMessage?: (data: unknown) => void;
    enabled?: boolean;
    reconnectAttempts?: number;
    reconnectInterval?: number;
}

interface UseWebSocketReturn {
    isConnected: boolean;
    send: (data: unknown) => void;
    disconnect: () => void;
}

export default function useWebSocket({
    url,
    onMessage,
    enabled = true,
    reconnectAttempts = 5,
    reconnectInterval = 3000,
}: UseWebSocketOptions): UseWebSocketReturn {
    const [isConnected, setIsConnected] = useState(false);
    const wsRef = useRef<WebSocket | null>(null);
    const attemptsRef = useRef(0);
    const timeoutRef = useRef<ReturnType<
        typeof setTimeout
    > | null>(null);
    const onMessageRef = useRef(onMessage);
    onMessageRef.current = onMessage;

    const cleanup = useCallback(() => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
        if (wsRef.current) {
            wsRef.current.onopen = null;
            wsRef.current.onclose = null;
            wsRef.current.onmessage = null;
            wsRef.current.onerror = null;
            wsRef.current.close();
            wsRef.current = null;
        }
        setIsConnected(false);
    }, []);

    const connect = useCallback(() => {
        cleanup();

        if (!enabled || !url) return;

        const ws = new WebSocket(url);
        wsRef.current = ws;

        ws.onopen = () => {
            setIsConnected(true);
            attemptsRef.current = 0;
        };

        ws.onmessage = (event) => {
            try {
                const data = JSON.parse(
                    event.data as string,
                );
                onMessageRef.current?.(data);
            } catch {
                onMessageRef.current?.(event.data);
            }
        };

        ws.onclose = () => {
            setIsConnected(false);
            wsRef.current = null;

            // Auto-reconnect with exponential backoff
            if (
                enabled
                && attemptsRef.current < reconnectAttempts
            ) {
                const delay = reconnectInterval
                    * (2 ** attemptsRef.current);
                attemptsRef.current += 1;
                timeoutRef.current = setTimeout(
                    connect,
                    delay,
                );
            }
        };

        ws.onerror = () => {
            // onclose will fire after onerror
        };
    }, [
        url,
        enabled,
        reconnectAttempts,
        reconnectInterval,
        cleanup,
    ]);

    useEffect(() => {
        if (enabled) {
            connect();
        } else {
            cleanup();
        }
        return cleanup;
    }, [enabled, connect, cleanup]);

    const send = useCallback((data: unknown) => {
        if (
            wsRef.current
            && wsRef.current.readyState === WebSocket.OPEN
        ) {
            wsRef.current.send(JSON.stringify(data));
        }
    }, []);

    return { isConnected, send, disconnect: cleanup };
}
